import styled, { css } from 'styled-components';
import { skeleton } from 'src/components/skeleton';
import { hover } from 'src/components/utils';
import { OnboardingLanguageFlag } from 'src/apps/Onboarding/OnboardingLanguageFlag';

export const Root = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    padding: ${theme.sz.s8} ${theme.sz.s8} ${theme.sz.s16};
    background-color: ${theme.co.blobBackground};

    @media (max-width: ${theme.sz.md}) {
      padding: ${theme.sz.s4} ${theme.sz.s5} ${theme.sz.s8};
    }
  `,
);

export const TopBar = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s2};
    min-height: ${theme.sz.s10};
  `,
);

export const BackButton = styled.button(
  ({ theme }) => css`
    position: absolute;
    inset-inline-start: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${theme.sz.s2};
    border: 0;
    border-radius: ${theme.bo.round};
    background: none;
    color: ${theme.co.neutral10};
    font-size: ${theme.sz.s6};
    cursor: pointer;

    &:hover {
      background-color: ${hover(theme.co.blobBackground)};
    }

    [dir='rtl'] & {
      transform: scaleX(-1);
    }

    @media (max-width: ${theme.sz.md}) {
      position: static;
      flex-shrink: 0;
      margin-inline-start: -${theme.sz.s2};
      /* Keeps the button at the start when there's no progress beside it */
      margin-inline-end: auto;
      font-size: ${theme.sz.s5};
    }

    ${theme.focusVisible};
  `,
);

export const Progress = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s4};

    @media (max-width: ${theme.sz.md}) {
      flex: 1;
      gap: ${theme.sz.s3};
    }
  `,
);

export const Segments = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s1};

    @media (max-width: ${theme.sz.md}) {
      flex: 1;
    }
  `,
);

export const Segment = styled.span<{ $filled: boolean }>(
  ({ theme, $filled }) => css`
    width: ${theme.sz.s26};
    height: ${theme.sz.s1};
    border-radius: ${theme.bo.round};
    background-color: ${$filled ? theme.co.primary20 : theme.co.neutral95};
    transition: background-color 0.3s ease;

    @media (max-width: ${theme.sz.md}) {
      flex: 1;
      width: auto;
    }
  `,
);

export const Count = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxxs};
    color: ${theme.co.neutral40};
    white-space: nowrap;
  `,
);

export const Content = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s8};
    width: 100%;
    margin: auto;
    padding-top: ${theme.sz.s16};

    @media (max-width: ${theme.sz.md}) {
      align-items: stretch;
      gap: ${theme.sz.s5};
      margin: 0;
      /* Leaves room for the CTA pinned to the bottom of the screen */
      padding: ${theme.sz.s6} 0 ${theme.sz.s32};
    }
  `,
);

export const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s3};
    text-align: center;

    @media (max-width: ${theme.sz.md}) {
      align-items: flex-start;
      gap: ${theme.sz.s2};
      text-align: start;
    }
  `,
);

export const Flag = styled(OnboardingLanguageFlag)(
  ({ theme }) => css`
    width: ${theme.sz.s16};
    height: ${theme.sz.s16};
    margin-bottom: ${theme.sz.s3};
    padding: ${theme.sz.s1};
    border: ${theme.bo.mediumWidth} solid ${theme.co.neutral50};
    background: ${theme.co.neutral99};

    @media (max-width: ${theme.sz.md}) {
      display: none;
    }
  `,
);

export const IconBadge = styled.span<{ $fullSize: boolean }>(
  ({ theme, $fullSize }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s20};
    height: ${theme.sz.s20};
    margin-bottom: ${theme.sz.s1};
    border-radius: ${theme.bo.round};
    background-color: ${$fullSize ? 'transparent' : theme.co.success99};
    color: ${theme.co.success30};
    font-size: ${$fullSize ? theme.sz.s20 : theme.sz.s8};

    @media (max-width: ${theme.sz.md}) {
      display: none;
    }
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.accent.xl};
    margin: 0;
    color: ${theme.co.neutral10};

    @media (max-width: ${theme.sz.md}) {
      ${theme.typography.heading.mobile.accent.xl};
      font-size: ${theme.sz.s7};
    }
  `,
);

export const Subtitle = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.l};
    margin: 0;
    color: ${theme.co.neutral40};

    @media (max-width: ${theme.sz.md}) {
      ${theme.typography.paragraph.m};
    }
  `,
);

export const SkeletonBone = styled.div(
  ({ theme }) => css`
    border-radius: ${theme.bo.small};
    ${skeleton};
  `,
);

export const SkeletonFlag = styled(SkeletonBone)(
  ({ theme }) => css`
    width: ${theme.sz.s16};
    height: ${theme.sz.s16};
    margin-bottom: ${theme.sz.s3};
    border-radius: ${theme.bo.round};

    @media (max-width: ${theme.sz.md}) {
      display: none;
    }
  `,
);

export const SkeletonTitle = styled(SkeletonBone)(
  ({ theme }) => css`
    width: min(100%, ${theme.sz.s48});
    height: ${theme.sz.s8};

    @media (max-width: ${theme.sz.md}) {
      height: ${theme.sz.s7};
    }
  `,
);

export const SkeletonSubtitle = styled(SkeletonBone)(
  ({ theme }) => css`
    width: min(100%, ${theme.sz.s32});
    height: ${theme.sz.s4};
  `,
);

export const SkeletonOptions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    width: 100%;
    max-width: ${theme.sz.ms};
  `,
);

export const SkeletonOption = styled(SkeletonBone)(
  ({ theme }) => css`
    width: 100%;
    height: ${theme.sz.s16};
    border-radius: ${theme.bo.medium};
  `,
);
